var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import ReactDOM from 'react-dom';
import React, { useState, useRef, useEffect } from 'react';
import { LocationFilterContainer } from '../styles/LocationFilter.styles';
import { LocationFilterHeader } from './LocationFilterHeader';
import { CookieConsentControl } from './CookieConsentControl';
import { LocationFilterBody } from './LocationFilterBody';
import { ConsentModal } from '@oneaudi/two-click';
import { useI18n } from '@oneaudi/i18n-context';
import { useServicesContext } from '../../../context';
import { getScsUrlPartsFromConfiguration } from '../../../hooks';
import { ExpiringStorage } from '@oneaudi/stck-store';
import { updateLocationSearchInputLocalStorage, setAddressFromLatLngGeocoder, initializeInitialStateFromLocalStorage, syncAutocompleteElementValue, } from '../utils';
import { FilterContext } from '../FilterContext';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { dynamicStylingHelper } from '../../utils';
import { SYS_COLOR_TEXT_COPY_DEFAULT_DARK } from '@oneaudi/unified-web-alpha-common';
import { createGlobalStyle } from 'styled-components';
import { useGoogleMaps } from '../hooks/loadGoogleLibraries';
import { useMandatoryAreaSearchContext } from '../hooks/MandatoryAreaSearchContext';
export const LocationFilter = ({ visibleIn, overlayRef }) => {
    var _a;
    const layerManager = useServicesContext().getAdditionalService('layer-manager');
    const logger = ((_a = useServicesContext().getAdditionalService('s2:logger')) !== null && _a !== void 0 ? _a : console);
    const { getConfiguration, getEnvironmentConfig } = useServicesContext();
    const scsUrlParts = getScsUrlPartsFromConfiguration(getConfiguration(), getEnvironmentConfig());
    const { enableMandatoryAreaSearch } = useMandatoryAreaSearchContext();
    const localStorageLocationSearchInputKey = enableMandatoryAreaSearch
        ? `VTP_${scsUrlParts === null || scsUrlParts === void 0 ? void 0 : scsUrlParts.marketPath}_mandatoryLocationSearch--input`
        : `VTP_${scsUrlParts === null || scsUrlParts === void 0 ? void 0 : scsUrlParts.marketPath}_locationSearch--input`;
    const context = React.useContext(FilterContext);
    const { setWholeMarketDealersFetch, radius, setRadius, searchedCoords, setSearchedCoords } = context;
    const { effectiveConsent, needConsent, libraryLoaded } = useGoogleMaps();
    const [twoClickOverlayOpened, setIsTwoClickOverlayOpened] = useState(false);
    const [map, setMap] = useState();
    const [locationInputValue, setLocationInputValue] = useState('');
    const inputRef = useRef(null);
    const changedByLocateMe = useRef(false);
    const autocompleteRef = useRef(null);
    const handleLocateMeClick = () => {
        changedByLocateMe.current = true;
        const options = {
            enableHighAccuracy: true,
            timeout: 30000,
        };
        const successCallback = (position) => __awaiter(void 0, void 0, void 0, function* () {
            if (map) {
                setAddressFromLatLngGeocoder(position, inputRef, localStorageLocationSearchInputKey, setLocationInputValue, context, autocompleteRef);
            }
            else {
                context.setSearchedCoords({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            }
        });
        const errorCallback = (error) => {
            logger === null || logger === void 0 ? void 0 : logger.error('There was a problem with fetching geolocation:', error);
        };
        navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
    };
    const [focusLayerNode, setFocusLayerNode] = useState();
    const [focusLayer, setFocusLayer] = useState();
    const focusLayerRef = React.useCallback((node) => {
        setFocusLayerNode(node);
    }, [setFocusLayerNode]);
    const openConsentOverlay = () => {
        const layer = layerManager.openFocusLayer(() => React.createElement("div", { ref: focusLayerRef }), {}, {
            userCloseable: true,
            size: FocusLayerSizeV2.B,
        });
        setFocusLayer(layer);
        setIsTwoClickOverlayOpened(true);
    };
    const mapConsentButtonText = useI18n({
        id: `nemo.sc.filter.locationfilter.cookieConsentRequestDealerList.Button`,
        defaultMessage: '',
    });
    const consentModalHeadline = useI18n({
        id: `google-maps-two-click-consent.headline`,
        defaultMessage: '',
    });
    const consentModalBody = useI18n({
        id: `google-maps-two-click-consent.copyText`,
        defaultMessage: '',
    });
    const consentModalToggle = useI18n({
        id: `google-maps-two-click-consent.toggleLabel`,
        defaultMessage: '',
    });
    const locationSearchDefaultInputLabel = useI18n({
        id: `nemo.sc.plp.quickLocationFilter.locationSearchDefaultInputLabel`,
        defaultMessage: '',
    });
    const localStorage = new ExpiringStorage(localStorageLocationSearchInputKey, 30);
    const inputDataFromLocalStorage = localStorage.readEntry();
    useEffect(() => {
        if (map && inputRef.current) {
            if (inputDataFromLocalStorage) {
                setWholeMarketDealersFetch(false);
                initializeInitialStateFromLocalStorage(inputRef, inputDataFromLocalStorage, setRadius, setSearchedCoords);
                setLocationInputValue(inputDataFromLocalStorage.inputName || '');
            }
            else {
                setWholeMarketDealersFetch(true);
            }
        }
    }, [map, inputRef.current]);
    useEffect(() => {
        if (radius)
            updateLocationSearchInputLocalStorage(localStorageLocationSearchInputKey, 'radius', radius);
    }, [radius]);
    useEffect(() => {
        if (locationInputValue) {
            updateLocationSearchInputLocalStorage(localStorageLocationSearchInputKey, 'inputName', locationInputValue);
        }
    }, [localStorageLocationSearchInputKey, locationInputValue]);
    useEffect(() => {
        if (effectiveConsent) {
            focusLayer === null || focusLayer === void 0 ? void 0 : focusLayer.close();
            setIsTwoClickOverlayOpened(false);
        }
    }, [effectiveConsent]);
    useEffect(() => {
        const savedValue = (inputDataFromLocalStorage === null || inputDataFromLocalStorage === void 0 ? void 0 : inputDataFromLocalStorage.inputName) || '';
        setLocationInputValue(savedValue);
        if (inputRef.current) {
            inputRef.current.value = savedValue;
        }
        if (libraryLoaded && autocompleteRef.current) {
            syncAutocompleteElementValue(autocompleteRef.current, savedValue);
            autocompleteRef.current.setAttribute('placeholder', locationSearchDefaultInputLabel);
        }
    }, [inputDataFromLocalStorage === null || inputDataFromLocalStorage === void 0 ? void 0 : inputDataFromLocalStorage.inputName, autocompleteRef.current, libraryLoaded]);
    useEffect(() => {
        var _a;
        setLocationInputValue(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.value) || '');
    }, [searchedCoords]);
    return (React.createElement(LocationFilterContainer, null,
        React.createElement(LocationFilterHeader, { inputRef: inputRef, autocompleteRef: autocompleteRef, locationInputValue: locationInputValue, mapConsentButtonText: mapConsentButtonText, openConsentOverlay: openConsentOverlay, setIsTwoClickOverlayOpened: setIsTwoClickOverlayOpened, setLocationInputValue: setLocationInputValue, handleLocateMeClick: handleLocateMeClick, overlayRef: overlayRef }),
        React.createElement(React.Fragment, null, twoClickOverlayOpened &&
            focusLayerNode &&
            ReactDOM.createPortal(React.createElement(React.Fragment, null,
                React.createElement(TwoClickStyle, null),
                React.createElement(ConsentModal, { theme: "light", headerText: consentModalHeadline, bodyText: consentModalBody, toggleText: consentModalToggle, showCloseButton: true, showRedirectButton: false, closeButtonCallback: () => {
                        focusLayer === null || focusLayer === void 0 ? void 0 : focusLayer.close();
                        setIsTwoClickOverlayOpened(false);
                    }, twoClickStyle: {
                        position: 'relative',
                        padding: 0,
                        top: 'unset',
                        transform: 'unset',
                        maxHeight: 'unset',
                        width: 'unset',
                        left: 'unset',
                    }, twoClickCancelCtaStyle: {
                        display: 'none',
                    }, vtp: true })), focusLayerNode)),
        React.createElement(LocationFilterBody, { consent: effectiveConsent, changedByLocateMe: changedByLocateMe, inputRef: inputRef, autocompleteRef: autocompleteRef, map: map, visibleIn: visibleIn, mapConsentButtonText: mapConsentButtonText, openConsentOverlay: openConsentOverlay, setIsTwoClickOverlayOpened: setIsTwoClickOverlayOpened, setMap: setMap, setLocationInputValue: setLocationInputValue }),
        needConsent && effectiveConsent && React.createElement(CookieConsentControl, { setMap: setMap })));
};
export default LocationFilter;
const TwoClickStyle = createGlobalStyle `
  .two-click-alpha {
    box-shadow: none!important;
    a {
      color: ${dynamicStylingHelper('#333333', SYS_COLOR_TEXT_COPY_DEFAULT_DARK)};
    }
  }
`;
